import { useEffect, useRef, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusPill } from '../../components/StatusPills';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-lg bg-ground p-5 min-w-0">
    <p className="pd-eyebrow">{label}</p>
    <p className="font-heading text-[32px] font-bold tracking-[-0.035em] leading-none text-ink mt-2" data-testid={testId}>
      {value ?? '—'}
    </p>
  </div>
);

const JOBS_POLL_MS = 4000;

const formatElapsed = (seconds) => {
  if (seconds == null) return '—';
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

/**
 * Live view of every document currently queued or being analysed, across
 * all users (see GET /admin/analysis/jobs, analysis_worker.py). Polls
 * while this page is open — there's no push channel for this, and it's a
 * small, infrequently-open admin page, so a short poll is the simplest
 * thing that works.
 */
const ActiveJobs = () => {
  const [jobs, setJobs] = useState(null);
  const [cancelling, setCancelling] = useState({}); // doc_id -> true while a cancel request is in flight
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await adminApi.analysisJobs();
        if (!cancelled) setJobs(res.data);
      } catch {
        /* keep showing the last known list rather than clearing it on a blip */
      }
    };
    tick();
    pollRef.current = setInterval(tick, JOBS_POLL_MS);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, []);

  const handleCancel = async (docId) => {
    setCancelling((prev) => ({ ...prev, [docId]: true }));
    try {
      const res = await adminApi.cancelAnalysisJob(docId);
      toast.success(res.data?.status === 'cancelled' ? 'Job cancelled.' : "Stopping — it'll finish its current step first.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That could not be cancelled.');
      setCancelling((prev) => ({ ...prev, [docId]: false }));
    }
  };

  const rows = jobs?.jobs || [];

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-medium text-foreground">Active jobs</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Documents being analysed right now, across every user.
            </p>
          </div>
          {jobs && (
            <p className="text-xs text-muted-foreground whitespace-nowrap" data-testid="jobs-summary">
              {jobs.queued_count} queued · {jobs.analysing_count} analysing
            </p>
          )}
        </div>

        {jobs === null ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Loading…
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="jobs-empty">Nothing running right now.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Worker</TableHead>
                <TableHead>Running</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((j) => (
                <TableRow key={j.id} data-testid={`job-row-${j.id}`}>
                  <TableCell className="font-mono text-xs text-foreground">
                    {j.id.slice(0, 8)}
                    <span className="block text-muted-foreground">{j.document_type || 'document'}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{j.user_id.slice(0, 8)}</TableCell>
                  <TableCell>
                    <StatusPill tone="held" size="sm">
                      {j.analysis_status === 'analysing' ? 'Analysing' : 'Queued'}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{j.analysis_stage || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {j.analysis_worker_id ? j.analysis_worker_id.split(':')[0] : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground font-mono">{formatElapsed(j.elapsed_seconds)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancel(j.id)}
                      disabled={!!cancelling[j.id]}
                      data-testid={`job-cancel-${j.id}`}
                    >
                      {cancelling[j.id] && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />}
                      Cancel
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

const AdminAnalysisOverview = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .analysisOverview()
      .then((res) => !cancelled && setOverview(res.data))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-analysis-overview">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">AI Analysis</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pipeline health, prompt versions, and flagged runs across all users.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Pipeline version{' '}
          <span className="font-mono text-foreground" data-testid="pipeline-version">
            {loading ? '…' : `v${overview?.pipeline_version ?? '?'}`}
          </span>
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          label="Total documents"
          value={loading ? '…' : overview?.total_documents}
          testId="stat-total-documents"
        />
        <StatCard
          label="Documents analysed"
          value={loading ? '…' : overview?.documents_analysed}
          testId="stat-analysed"
        />
        <StatCard
          label="Documents failed"
          value={loading ? '…' : overview?.documents_failed}
          testId="stat-failed"
        />
        <StatCard
          label="Pending document types"
          value={loading ? '…' : overview?.pending_document_types}
          testId="stat-pending-doc-types"
        />
        <StatCard
          label="Pending library tags"
          value={loading ? '…' : overview?.pending_library_tags}
          testId="stat-pending-tags"
        />
      </div>

      <ActiveJobs />
    </div>
  );
};

export default AdminAnalysisOverview;
